import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { trackEvent } from '../lib/gamification';
import { handlePlanLimitError } from '../lib/planLimit';
import { 
  ArrowLeft, HelpCircle, ChevronRight, Euro, Home, GraduationCap, 
  Briefcase, Calculator, Sparkles, Loader2, CheckCircle2, 
  ExternalLink, Plus, TrendingUp, Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AidesPage = () => {
  const { isDark } = useTheme();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [showCalculator, setShowCalculator] = useState(false);
  const [loading, setLoading] = useState(false);
  const [calculationResult, setCalculationResult] = useState(null);
  const [tasksCreating, setTasksCreating] = useState(false);
  const [profile, setProfile] = useState({
    age: 22,
    situation: 'etudiant',
    revenus_mensuels: 800,
    loyer: 500,
    code_postal: '75011',
    situation_familiale: 'celibataire',
    nb_enfants: 0,
    boursier: false
  });

  // Load existing profile on mount
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data && Object.keys(response.data).length > 0) {
          setProfile(prev => ({ ...prev, ...response.data }));
        }
      } catch (error) {
        console.log('No existing profile');
      }
    };
    if (token) loadProfile();
  }, [token]);

  const handleCalculate = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/aids/calculate`, profile, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCalculationResult(response.data);
      toast.success('Simulation terminée !');
      trackEvent(token, 'aids_simulator');
      // If at least one aid is eligible, also award the eligibility badge
      if (response.data?.aides_estimees?.some?.((a) => a.eligible)) {
        trackEvent(token, 'aids_eligible');
      }
    } catch (error) {
      if (!handlePlanLimitError(error, navigate)) {
        toast.error('Erreur lors du calcul');
        console.error(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTasks = async () => {
    setTasksCreating(true);
    try {
      const response = await axios.post(`${API_URL}/api/aids/create-tasks`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Erreur lors de la création des tâches');
    } finally {
      setTasksCreating(false);
    }
  };

  const getAideIcon = (code) => {
    const icons = {
      'apl': Home,
      'als': Home,
      'prime_activite': Euro,
      'bourse_crous': GraduationCap,
      'rsa': Briefcase,
      'rsa_jeune': Briefcase,
      'css': HelpCircle,
      'cheque_energie': Zap
    };
    return icons[code] || HelpCircle;
  };

  return (
    <div className={`min-h-screen px-4 py-6 transition-colors duration-300 ${isDark ? 'bg-[#0F0F1A]' : 'bg-[#FAFBFC]'}`} data-testid="aides-page">
      <div className="max-w-4xl mx-auto">
        <Link 
          to="/dashboard" 
          className={`inline-flex items-center gap-2 mb-6 transition-colors ${
            isDark ? 'text-[#94A3B8] hover:text-[#81d8d0]' : 'text-gray-500 hover:text-[#81d8d0]'
          }`}
          data-testid="back-to-dashboard"
        >
          <ArrowLeft size={18} />
          <span>Retour au dashboard</span>
        </Link>

        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-2xl bg-[#81d8d0]/20 flex items-center justify-center">
              <Calculator className="w-6 h-6 text-[#81d8d0]" />
            </div>
            <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Simulateur d'Aides
            </h1>
          </div>
          <p className={`mb-8 ${isDark ? 'text-[#94A3B8]' : 'text-gray-600'}`}>
            Calcule les aides auxquelles tu as droit en temps réel
          </p>
        </motion.div>

        {/* Result Banner */}
        {calculationResult && calculationResult.success && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
            <Card className={`p-6 mb-6 border ${
              isDark 
                ? 'bg-gradient-to-r from-[#81d8d0]/20 to-[#4ECDC4]/10 border-[#81d8d0]/30' 
                : 'bg-gradient-to-r from-[#81d8d0]/10 to-[#4ECDC4]/5 border-[#81d8d0]/40'
            }`}>
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="text-center md:text-left">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="text-[#81d8d0]" size={20} />
                    <p className={`text-sm ${isDark ? 'text-[#94A3B8]' : 'text-gray-500'}`}>Estimation potentielle</p>
                  </div>
                  <p className="text-4xl font-bold text-[#81d8d0]">
                    ~{Math.round(calculationResult.total_mensuel)}€<span className="text-lg">/mois</span>
                  </p>
                  <p className={`text-sm mt-1 ${isDark ? 'text-[#94A3B8]' : 'text-gray-500'}`}>
                    soit environ {Math.round(calculationResult.total_annuel).toLocaleString('fr-FR')}€/an
                  </p>
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={handleCreateTasks}
                    disabled={tasksCreating}
                    className="bg-[#81d8d0] hover:bg-[#6bc4bc] text-[#0F0F1A] font-semibold"
                    data-testid="create-tasks-btn"
                  >
                    {tasksCreating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus size={18} className="mr-2" />}
                    Créer les tâches
                  </Button>
                </div>
              </div>
              {/* Disclaimer */}
              <div className={`mt-4 pt-4 border-t text-xs ${isDark ? 'border-white/10 text-[#64748B]' : 'border-gray-200 text-gray-500'}`}>
                <span className="inline-block mb-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#81d8d0]/15 text-[#81d8d0]" data-testid="aids-source">
                  {calculationResult.source === 'openfisca_api' ? 'Calcul OpenFisca (barèmes officiels)' : 'Estimation locale Skipya (non officielle)'}
                </span>
                <br />
                ⚠️ {calculationResult.disclaimer || "Ces montants sont des estimations."} Le montant réel dépend de ta situation exacte et sera déterminé par l'organisme concerné (CAF, CROUS, etc.).
                {calculationResult.hypotheses?.length > 0 && (
                  <ul className="mt-2 list-disc pl-4 space-y-0.5" data-testid="aids-hypotheses">
                    {calculationResult.hypotheses.map((h) => <li key={h}>{h}</li>)}
                  </ul>
                )}
              </div>
            </Card>
          </motion.div>
        )}

        {/* Calculator Form */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card className={`p-6 mb-8 border ${
            isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'
          }`}>
            <div className="flex items-center gap-3 mb-6">
              <Sparkles className="text-[#81d8d0]" size={24} />
              <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Ton profil
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <Label className={isDark ? 'text-[#94A3B8]' : 'text-gray-600'}>Âge</Label>
                <Input
                  type="number"
                  value={profile.age}
                  onChange={(e) => setProfile({ ...profile, age: parseInt(e.target.value) || 18 })}
                  className={`mt-1 ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-200'}`}
                  data-testid="input-age"
                />
              </div>

              <div>
                <Label className={isDark ? 'text-[#94A3B8]' : 'text-gray-600'}>Situation</Label>
                <Select value={profile.situation} onValueChange={(val) => setProfile({ ...profile, situation: val })}>
                  <SelectTrigger className={`mt-1 ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-200'}`} data-testid="select-situation">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="etudiant">Étudiant</SelectItem>
                    <SelectItem value="salarie">Salarié</SelectItem>
                    <SelectItem value="chomeur">Demandeur d'emploi</SelectItem>
                    <SelectItem value="independant">Indépendant</SelectItem>
                    <SelectItem value="inactif">Sans activité</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className={isDark ? 'text-[#94A3B8]' : 'text-gray-600'}>Revenus mensuels (€)</Label>
                <Input
                  type="number"
                  value={profile.revenus_mensuels}
                  onChange={(e) => setProfile({ ...profile, revenus_mensuels: parseFloat(e.target.value) || 0 })}
                  className={`mt-1 ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-200'}`}
                  data-testid="input-revenus"
                />
              </div>

              <div>
                <Label className={isDark ? 'text-[#94A3B8]' : 'text-gray-600'}>Loyer mensuel (€)</Label>
                <Input
                  type="number"
                  value={profile.loyer}
                  onChange={(e) => setProfile({ ...profile, loyer: parseFloat(e.target.value) || 0 })}
                  className={`mt-1 ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-200'}`}
                  data-testid="input-loyer"
                />
              </div>

              <div>
                <Label className={isDark ? 'text-[#94A3B8]' : 'text-gray-600'}>Situation familiale</Label>
                <Select value={profile.situation_familiale} onValueChange={(val) => setProfile({ ...profile, situation_familiale: val })}>
                  <SelectTrigger className={`mt-1 ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-200'}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="celibataire">Célibataire</SelectItem>
                    <SelectItem value="couple">En couple</SelectItem>
                    <SelectItem value="famille">Avec enfants</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className={isDark ? 'text-[#94A3B8]' : 'text-gray-600'}>Code postal</Label>
                <Input
                  type="text"
                  value={profile.code_postal}
                  onChange={(e) => setProfile({ ...profile, code_postal: e.target.value })}
                  className={`mt-1 ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-200'}`}
                  data-testid="input-cp"
                />
              </div>
            </div>

            <Button
              onClick={handleCalculate}
              disabled={loading}
              className="w-full bg-[#81d8d0] hover:bg-[#6bc4bc] text-[#0F0F1A] font-semibold py-5"
              data-testid="calculate-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Calcul en cours...
                </>
              ) : (
                <>
                  <Calculator size={20} className="mr-2" />
                  Calculer mes aides
                </>
              )}
            </Button>
          </Card>
        </motion.div>

        {/* Results List */}
        {calculationResult && calculationResult.aides_estimees && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Aides détectées
            </h2>
            <div className="space-y-3">
              {calculationResult.aides_estimees.map((aide, index) => {
                const Icon = getAideIcon(aide.code);
                return (
                  <Card 
                    key={index}
                    className={`p-4 border transition-all ${
                      aide.eligible
                        ? isDark 
                          ? 'bg-[#81d8d0]/10 border-[#81d8d0]/30 hover:border-[#81d8d0]/50' 
                          : 'bg-[#81d8d0]/5 border-[#81d8d0]/30 hover:border-[#81d8d0]/50'
                        : isDark
                          ? 'bg-white/5 border-white/10 opacity-60'
                          : 'bg-gray-50 border-gray-200 opacity-60'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        aide.eligible ? 'bg-[#81d8d0]/20' : isDark ? 'bg-white/10' : 'bg-gray-200'
                      }`}>
                        <Icon className={aide.eligible ? 'text-[#81d8d0]' : isDark ? 'text-[#64748B]' : 'text-gray-400'} size={24} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{aide.nom}</h4>
                          {aide.eligible && <CheckCircle2 size={16} className="text-[#81d8d0]" />}
                        </div>
                        <p className={`text-sm ${isDark ? 'text-[#94A3B8]' : 'text-gray-500'}`}>
                          {aide.description}
                        </p>
                        <p className={`text-xs mt-1 ${isDark ? 'text-[#64748B]' : 'text-gray-400'}`}>
                          {aide.organisme} • {aide.demarche}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className={`text-lg font-bold ${aide.eligible ? 'text-[#81d8d0]' : isDark ? 'text-[#64748B]' : 'text-gray-400'}`}>
                          {aide.eligible ? `+${Math.round(aide.montant_mensuel)}€` : '--'}
                        </p>
                        <p className={`text-xs ${isDark ? 'text-[#64748B]' : 'text-gray-400'}`}>/mois</p>
                      </div>
                      {aide.url && aide.eligible && (
                        <a 
                          href={aide.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                        >
                          <ExternalLink size={18} className="text-[#81d8d0]" />
                        </a>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Info Stats */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mt-8">
          <Card className={`p-6 text-center border ${
            isDark ? 'bg-[#FF6B6B]/10 border-[#FF6B6B]/20' : 'bg-[#FF6B6B]/5 border-[#FF6B6B]/20'
          }`}>
            <p className="text-4xl font-bold text-[#FF6B6B] mb-2">10 Milliards €</p>
            <p className={isDark ? 'text-[#94A3B8]' : 'text-gray-600'}>
              d'aides non réclamées chaque année en France. Ne fais pas partie des oubliés !
            </p>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default AidesPage;
